import { Suspense, useState, useEffect, useRef, Component, ErrorInfo, ReactNode } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment } from '../experience/Environment';
import { MallHub } from '../experience/MallHub';
import { StoreScene } from '../experience/StoreScene';
import { ProductSpotlight } from '../experience/ProductSpotlight';
import { CameraController } from '../systems/CameraController';
import { Loader } from '@react-three/drei';
import { ExperienceTransitions } from '../experience/ExperienceTransitions';
import { EffectComposer, Bloom, ChromaticAberration, Noise, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';

import { PlanetEarth } from '../experience/PlanetEarth';
import { GymScene } from '../experience/GymScene';

// Fallback & Store Imports
import { motion, AnimatePresence } from 'motion/react';
import { useExperienceStore } from '../state/useExperienceStore';
import { useGymStore, GYM_PRODUCTS } from '../state/useGymStore';
import { 
  Dumbbell, 
  ShoppingBag, 
  Globe, 
  Compass, 
  Activity, 
  ArrowRight, 
  Sparkles, 
  ShieldCheck, 
  Zap, 
  Flame,
  LayoutGrid,
  Target
} from 'lucide-react';

// WebGL Compatibility detector
const checkWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

// Canvas Custom Error Boundary
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onError?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class CanvasErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false
  };

  public static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn("R3F Canvas WebGL Error captured gracefully:", error, errorInfo);
    if (this.props.onError) {
      this.props.onError();
    }
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Universal 2D Particle Canvas Background
const BackgroundParticles2D = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const particles: { x: number; y: number; r: number; dx: number; dy: number; alpha: number }[] = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.5 + 1,
        dx: (Math.random() - 0.5) * 0.3,
        dy: (Math.random() - 0.5) * 0.3,
        alpha: Math.random() * 0.5 + 0.1
      });
    }

    const handleResize = () => {
      if (canvas) {
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', handleResize);

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(5, 11, 20, 1)';
      ctx.fillRect(0, 0, width, height);

      // Technical blueprint grid lines
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.025)';
      ctx.lineWidth = 1;
      const gridSize = 80;
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Atmospheric radial glow
      const grad = ctx.createRadialGradient(width / 2, height / 2, 20, width / 2, height / 2, Math.max(width, height) / 2);
      grad.addColorStop(0, 'rgba(6, 182, 212, 0.04)');
      grad.addColorStop(0.5, 'rgba(99, 102, 241, 0.015)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Animate star particles
      for (const p of particles) {
        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(6, 182, 212, ${p.alpha})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
};

// Premium Fallback Dashboard
const ExperienceFallback2D = () => {
  const mode = useExperienceStore((state) => state.mode);
  const setIntroComplete = useExperienceStore((state) => state.setIntroComplete);
  const selectedProductId = useExperienceStore((state) => state.selectedProductId);

  return (
    <div className="fixed inset-0 z-0 bg-[#050b14] overflow-hidden select-none">
      <BackgroundParticles2D />

      {/* Futuristic Hologram Active Indicator */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/80 backdrop-blur-md px-4 py-1.5 rounded-full border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.15)]">
        <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
        <span className="text-[9px] font-black uppercase tracking-widest text-cyan-400">
          INTERFAZ OPTIMIZADA (2.5D SECURE INTERFACE)
        </span>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'INTRO' && (
          <motion.div 
            key="fallback-intro"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="relative flex flex-col items-center justify-center h-full z-10 px-4"
          >
            <div className="text-center max-w-2xl">
              <div className="relative w-40 h-40 mx-auto mb-8 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-cyan-400/20 animate-[spin_40s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border border-indigo-400/30 animate-[spin_20s_linear_infinite_reverse]" />
                <div className="absolute inset-8 rounded-full border-t-2 border-cyan-400 animate-spin" />
                <Compass className="w-14 h-14 text-cyan-400 animate-pulse" />
              </div>
              <h1 className="text-4xl md:text-6xl font-black italic uppercase tracking-tighter text-white mb-3 leading-none">
                VIRTUAL <span className="text-cyan-400">TRAINING</span> & WIKI
              </h1>
              <p className="text-white/40 text-[9px] md:text-xs font-black uppercase tracking-[0.25em] mb-10">
                Plataforma Inmersiva de Alto Rendimiento para Atletas de Élite
              </p>
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(6,182,212,0.4)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIntroComplete(true)}
                className="px-10 py-5 bg-cyan-500 text-black text-xs font-black uppercase tracking-[0.3em] rounded-full shadow-[0_0_15px_rgba(6,182,212,0.2)] transition-all duration-300"
              >
                Iniciar Experiencia 2.5D →
              </motion.button>
            </div>
          </motion.div>
        )}

        {(mode === 'PLANET' || mode === 'CONTINENT' || mode === 'COUNTRY') && (
          <motion.div 
            key="fallback-planet"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col items-center justify-center h-full z-10 px-4 relative overflow-y-auto pt-24 pb-12 custom-scrollbar"
          >
            <div className="max-w-4xl w-full">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter text-white">
                  PORTAL DE <span className="text-cyan-400">REDIRECCIÓN GLOBAL</span>
                </h2>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">
                  Selecciona tu región para sintonizar los servidores locales
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { country: "España", code: "ESP", flag: "🇪🇸", continent: "Europa", color: "from-amber-500/10 to-transparent", desc: "Sede principal europea de entrenamiento." },
                  { country: "Colombia", code: "COL", flag: "🇨🇴", continent: "América", color: "from-yellow-500/10 to-transparent", desc: "Hub de comunidad fitness y atletismo." },
                  { country: "México", code: "MEX", flag: "🇲🇽", continent: "América", color: "from-green-500/10 to-transparent", desc: "Servidores olímpicos y rutinas de alta densidad." },
                  { country: "Argentina", code: "ARG", flag: "🇦🇷", continent: "América", color: "from-sky-500/10 to-transparent", desc: "Centro de kinesiología y biomecánica avanzada." }
                ].map((loc) => (
                  <motion.button
                    key={loc.country}
                    whileHover={{ y: -4 }}
                    onClick={() => {
                      useExperienceStore.getState().selectCountry(loc.country);
                      useExperienceStore.getState().selectContinent(loc.continent);
                      useExperienceStore.getState().setMode('HUB');
                    }}
                    className="bg-black/60 border border-white/5 hover:border-cyan-500/30 p-6 rounded-2xl text-left transition-all relative overflow-hidden group flex flex-col justify-between h-52"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-b ${loc.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-3xl">{loc.flag}</span>
                        <span className="text-[9px] font-black text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">{loc.code}</span>
                      </div>
                      <h3 className="text-lg font-black text-white italic uppercase tracking-tighter mb-1 leading-none">{loc.country}</h3>
                      <p className="text-white/30 text-[9px] uppercase tracking-widest font-mono mb-2">{loc.continent}</p>
                      <p className="text-white/40 text-[10px] leading-relaxed font-medium uppercase">{loc.desc}</p>
                    </div>
                    <div className="relative z-10 pt-3 border-t border-white/5 flex items-center justify-between text-cyan-400 text-[8px] font-black uppercase tracking-widest">
                      <span>Sintonizar canal</span>
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {mode === 'HUB' && (
          <motion.div 
            key="fallback-hub"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col items-center justify-center h-full z-10 px-4 relative overflow-y-auto pt-24 pb-12 custom-scrollbar"
          >
            <div className="max-w-4xl w-full">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter text-white">
                  CENTRO DE <span className="text-cyan-400">OPERACIONES (HUB)</span>
                </h2>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">
                  Explora las instalaciones interactivas de kinesiología y fitness
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <motion.button
                  whileHover={{ y: -5 }}
                  onClick={() => useExperienceStore.getState().setMode('GYM')}
                  className="bg-black/60 border border-cyan-500/20 hover:border-cyan-400 p-6 rounded-3xl text-left relative overflow-hidden group h-72 flex flex-col justify-between"
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.05] to-transparent opacity-50 group-hover:from-cyan-400/[0.1] transition-colors" />
                  <div className="relative z-10">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-4 group-hover:scale-105 transition-transform">
                      <Dumbbell className="w-5 h-5" />
                    </div>
                    <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest block mb-1">INSTALACIÓN BIOMECÁNICA</span>
                    <h3 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2">GIMNASIO INTELIGENTE (WIKI)</h3>
                    <p className="text-white/40 text-[10px] leading-relaxed uppercase font-medium">
                      Anatomía interactiva. Rutinas de entrenamiento estructuradas por kinefilaxia y biomecánica deportiva.
                    </p>
                  </div>
                  <div className="relative z-10 pt-4 border-t border-white/5 flex items-center justify-between text-cyan-400 text-[9px] font-black uppercase tracking-widest">
                    <span>ENTRAR AL GIMNASIO</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ y: -5 }}
                  onClick={() => useExperienceStore.getState().selectShop('shop-mall')}
                  className="bg-black/60 border border-indigo-500/20 hover:border-indigo-400 p-6 rounded-3xl text-left relative overflow-hidden group h-72 flex flex-col justify-between"
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.05] to-transparent opacity-50 group-hover:from-indigo-400/[0.1] transition-colors" />
                  <div className="relative z-10">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 mb-4 group-hover:scale-105 transition-transform">
                      <ShoppingBag className="w-5 h-5" />
                    </div>
                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest block mb-1">PRODUCTOS DE RENDIMIENTO</span>
                    <h3 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2">TIENDA VIRTUAL ATLETAS</h3>
                    <p className="text-white/40 text-[10px] leading-relaxed uppercase font-medium">
                      Explora indumentaria de compresión, avena instantánea de alta densidad y suplementación avanzada.
                    </p>
                  </div>
                  <div className="relative z-10 pt-4 border-t border-white/5 flex items-center justify-between text-indigo-400 text-[9px] font-black uppercase tracking-widest">
                    <span>ENTRAR A LA TIENDA</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ y: -5 }}
                  onClick={() => {
                    const selector = document.getElementById('location-selector-trigger');
                    if (selector) (selector as HTMLElement).click();
                  }}
                  className="bg-black/60 border border-white/5 hover:border-white/20 p-6 rounded-3xl text-left relative overflow-hidden group h-72 flex flex-col justify-between"
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/[0.01] to-transparent opacity-50 group-hover:from-white/[0.03] transition-colors" />
                  <div className="relative z-10">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 mb-4 group-hover:scale-105 transition-transform">
                      <Globe className="w-5 h-5" />
                    </div>
                    <span className="text-[8px] font-black text-white/40 uppercase tracking-widest block mb-1">LOCALIZACIÓN GLOBAL</span>
                    <h3 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2">SINTONIZADOR DE REGIONES</h3>
                    <p className="text-white/40 text-[10px] leading-relaxed uppercase font-medium">
                      Cambia tu ubicación geográfica o busca tiendas locales de la comunidad para entrenamiento y running.
                    </p>
                  </div>
                  <div className="relative z-10 pt-4 border-t border-white/5 flex items-center justify-between text-white/40 text-[9px] font-black uppercase tracking-widest group-hover:text-white">
                    <span>SELECCIONAR PAÍS</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {mode === 'STORE' && (
          <motion.div 
            key="fallback-store"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col items-center justify-center h-full z-10 px-4 relative overflow-y-auto pt-24 pb-12 custom-scrollbar"
          >
            <div className="max-w-4xl w-full">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <button 
                    onClick={() => useExperienceStore.getState().setMode('HUB')}
                    className="text-[9px] font-black text-cyan-400 uppercase tracking-[0.2em] hover:underline flex items-center gap-1 mb-1.5"
                  >
                    ← Volver al Hub
                  </button>
                  <h2 className="text-2xl md:text-4xl font-black italic uppercase tracking-tighter text-white leading-none">
                    VITRINA DE <span className="text-cyan-400">ALTO RENDIMIENTO</span>
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {GYM_PRODUCTS.map((prod) => (
                  <motion.div
                    key={prod.id}
                    whileHover={{ y: -3 }}
                    className="bg-black/60 border border-white/5 hover:border-cyan-500/20 rounded-2xl p-4 flex flex-col justify-between h-[340px] relative overflow-hidden group"
                  >
                    <div>
                      <div className="relative h-40 rounded-xl overflow-hidden mb-3 bg-white/5">
                        <img 
                          src={prod.image} 
                          alt={prod.name} 
                          className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500" 
                          referrerPolicy="no-referrer"
                        />
                        <span className="absolute top-3 left-3 bg-black/80 backdrop-blur-md px-2.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider text-cyan-400 border border-cyan-500/20">
                          {prod.category}
                        </span>
                      </div>
                      <h3 className="text-base font-black text-white italic uppercase tracking-tighter line-clamp-1 group-hover:text-cyan-300 transition-colors leading-none mb-1">
                        {prod.name}
                      </h3>
                      <p className="text-white/40 text-[10px] line-clamp-2 uppercase font-medium leading-relaxed">
                        {prod.description}
                      </p>
                    </div>
                    <div className="pt-3 border-t border-white/5 flex items-center justify-between mt-auto">
                      <span className="text-lg font-black text-cyan-400">${prod.price}</span>
                      <button
                        onClick={() => useExperienceStore.getState().selectProduct(prod.id)}
                        className="bg-cyan-500 text-black px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-cyan-400 transition-colors flex items-center gap-1"
                      >
                        Inspeccionar
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {mode === 'PRODUCT' && (
          <motion.div 
            key="fallback-product"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col items-center justify-center h-full z-10 px-4 relative overflow-y-auto pt-24 pb-12 custom-scrollbar"
          >
            {(() => {
              const selectedProductId = useExperienceStore((s) => s.selectedProductId);
              const prod = GYM_PRODUCTS.find(p => p.id === selectedProductId) || GYM_PRODUCTS[0];
              if (!prod) return null;

              return (
                <div className="max-w-3xl w-full bg-black/60 border border-white/5 rounded-3xl p-6 md:p-8 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/[0.02] to-indigo-500/[0.02] pointer-events-none" />
                  
                  <button 
                    onClick={() => useExperienceStore.getState().selectProduct(null)}
                    className="text-[9px] font-black text-cyan-400 uppercase tracking-widest hover:underline flex items-center gap-1 mb-6 relative z-10"
                  >
                    ← Volver a la Tienda
                  </button>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 relative z-10">
                    <div className="flex flex-col justify-center items-center">
                      <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-white/5 border border-white/10 group shadow-2xl flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 via-transparent to-transparent pointer-events-none" />
                        <img 
                          src={prod.image} 
                          alt={prod.name} 
                          className="w-full h-full object-cover" 
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider text-cyan-400">
                            {prod.category}
                          </span>
                          <span className="bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider text-indigo-300 font-mono">
                            ID: {prod.id.toUpperCase()}
                          </span>
                        </div>
                        
                        <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-3 leading-tight">
                          {prod.name}
                        </h2>
                        
                        <p className="text-white/50 text-[10px] md:text-xs uppercase leading-relaxed font-medium mb-5">
                          {prod.description}
                        </p>

                        <div className="space-y-2.5 bg-white/[0.02] border border-white/5 p-4 rounded-xl mb-6">
                          <div className="flex items-center justify-between text-[8px] font-black uppercase tracking-widest text-white/40">
                            <span>ACTIVACIÓN MUSCULAR</span>
                            <span className="text-white">ALTO RECLUTAMIENTO</span>
                          </div>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="w-[85%] h-full bg-cyan-400" />
                          </div>
                          <div className="flex items-center justify-between text-[8px] font-black uppercase tracking-widest text-white/40">
                            <span>ABSORCIÓN / ERGONOMÍA</span>
                            <span className="text-white">OPTIMIZADO</span>
                          </div>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="w-[95%] h-full bg-indigo-400" />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-5 border-t border-white/5">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-black text-white/30 uppercase tracking-widest">PRECIO</span>
                          <span className="text-2xl font-black text-cyan-400 leading-none">${prod.price}</span>
                        </div>
                        <button
                          onClick={() => {
                            const pts = Math.floor(prod.price * 2);
                            useGymStore.getState().addPoints(pts);
                            alert(`¡Felicidades! Has adquirido ${prod.name}. Se han añadido ${pts} puntos a tu Tarjeta de Fidelidad.`);
                          }}
                          className="bg-cyan-500 text-black px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all flex items-center gap-1.5"
                        >
                          COMPRAR
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

        {mode === 'GYM' && (
          <motion.div 
            key="fallback-gym"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col items-center justify-center h-full z-10 px-4 relative overflow-y-auto pt-24 pb-12 custom-scrollbar"
          >
            <div className="max-w-4xl w-full">
              <div className="text-center mb-8">
                <button 
                  onClick={() => useExperienceStore.getState().setMode('HUB')}
                  className="text-[9px] font-black text-cyan-400 uppercase tracking-widest hover:underline flex items-center gap-1 mx-auto mb-1.5"
                >
                  ← Volver al Hub
                </button>
                <h2 className="text-2xl md:text-4xl font-black italic uppercase tracking-tighter text-white leading-none">
                  SINTONIZADOR DE <span className="text-cyan-400">ZONAS DE ENTRENAMIENTO</span>
                </h2>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">
                  Elige un grupo muscular para entrenar y ver la ciencia de cada ejercicio
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { id: 'chest', name: 'Pecho / Pectorales', icon: <Target className="w-4 h-4 text-cyan-400" />, desc: 'Fuerza de empuje, pectorales.' },
                  { id: 'back', name: 'Espalda / Dorsales', icon: <LayoutGrid className="w-4 h-4 text-cyan-400" />, desc: 'Dorsales y trapecios.' },
                  { id: 'shoulders', name: 'Hombros / Deltoides', icon: <Zap className="w-4 h-4 text-cyan-400" />, desc: 'Movilidad articular.' },
                  { id: 'biceps', name: 'Bíceps / Brazos', icon: <Dumbbell className="w-4 h-4 text-cyan-400" />, desc: 'Flexión de codo.' },
                  { id: 'triceps', name: 'Tríceps / Brazos', icon: <Dumbbell className="w-4 h-4 text-cyan-400" />, desc: 'Extensión de codo.' },
                  { id: 'core', name: 'Core / Abdominales', icon: <Activity className="w-4 h-4 text-cyan-400" />, desc: 'Estabilidad central.' },
                  { id: 'quads', name: 'Piernas / Cuádriceps', icon: <Flame className="w-4 h-4 text-cyan-400" />, desc: 'Extensores de rodilla.' },
                  { id: 'glutes', name: 'Glúteos / Cadena Post.', icon: <Sparkles className="w-4 h-4 text-cyan-400" />, desc: 'Estabilidad de cadera.' }
                ].map((mc) => (
                  <motion.button
                    key={mc.id}
                    whileHover={{ y: -3 }}
                    onClick={() => {
                      useGymStore.getState().setSelectedMuscle(mc.id as any);
                    }}
                    className="bg-black/60 border border-white/5 hover:border-cyan-500/30 p-5 rounded-2xl text-left transition-all relative overflow-hidden group flex flex-col justify-between h-36"
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                        {mc.icon}
                      </div>
                      <span className="text-[9px] text-cyan-400 font-bold">ENTRENAR →</span>
                    </div>
                    <div>
                      <h3 className="text-base font-black text-white italic uppercase tracking-tighter group-hover:text-cyan-300 transition-colors leading-none mb-1">
                        {mc.name}
                      </h3>
                      <p className="text-white/40 text-[9px] line-clamp-1 uppercase font-medium">
                        {mc.desc}
                      </p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ExperienceContent = () => {
  return (
    <>
      <CameraController key="camera-controller" />
      <ExperienceTransitions key="transitions" />
      <Suspense fallback={null}>
        <Environment />
        <PlanetEarth />
        <MallHub />
        <StoreScene />
        <ProductSpotlight />
        <GymScene />
        
        <EffectComposer enableNormalPass={false}>
          <Bloom 
            intensity={1.5} 
            luminanceThreshold={0.5} 
            luminanceSmoothing={0.9} 
            height={300} 
          />
          <ChromaticAberration 
            offset={new THREE.Vector2(0.001, 0.001)} 
            radialModulation={false}
            modulationOffset={0}
          />
          <Noise opacity={0.02} />
          <Vignette eskil={false} offset={0.1} darkness={1.1} />
        </EffectComposer>
      </Suspense>
    </>
  );
};

export const Experience = () => {
  const [webGlSupported, setWebGlSupported] = useState<boolean | null>(null);

  // Safely check WebGL support on component mount
  useEffect(() => {
    setWebGlSupported(checkWebGLSupport());
  }, []);

  if (webGlSupported === null) {
    return <div className="fixed inset-0 z-0 bg-black flex items-center justify-center text-cyan-400 font-mono text-xs uppercase tracking-widest animate-pulse">Iniciando Biomecánica...</div>;
  }

  // Render the beautiful 2.5D interface fallback if WebGL is unsupported
  if (!webGlSupported) {
    return <ExperienceFallback2D />;
  }

  return (
    <CanvasErrorBoundary 
      fallback={<ExperienceFallback2D />}
      onError={() => setWebGlSupported(false)}
    >
      <div className="fixed inset-0 z-0 bg-black">
        <Canvas
          shadows
          camera={{ position: [0, 80, 500], fov: 45 }}
          gl={{ 
            antialias: true, 
            toneMapping: THREE.ACESFilmicToneMapping,
            outputColorSpace: THREE.SRGBColorSpace,
            failIfMajorPerformanceCaveat: false
          }}
        >
          <ExperienceContent />
        </Canvas>
        <Loader />
      </div>
    </CanvasErrorBoundary>
  );
};

